/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import { CenteredLoader, captureException } from '@playbookux/playbook-web-core';

import {
  Button,
  ButtonContainer,
  CloseIcon,
  Container,
  DisqualifiedContainer,
  Error,
  Footer,
  Modal,
  QuestionCounter,
  RadioGroup,
  Text,
  TextContainer,
  TextPrimaryBold,
  Title,
} from './styles';

import { isLastQuestion } from '../../helpers';

import { ANSWER_SCREENER_SURVEY, CONSENT_FORM_ANSWER_BY_SEGMENT_AND_PROJECT_ID } from '../../../../../core/graphql/screener-sessions';

import Options from './options';
import ConsentFormPrompt from '../../../../components/consent-form-prompt';
import { validateScreenerAnswers } from '../../../../../core/utils/validate-screener-answers';
import screenerSessionResults from '../../../../../core/constants/screener-session-result';

export const TYPE_QUESTIONS = {
  SINGLE_SELECT: 'single_select',
  MULTIPLE_SELECT: 'multiple_select',
  RANGE: 'range',
  WRITTEN: 'written',
};

const ScreenerModal = (props) => {
  const {
    testerStudy = {},
    onStudyRejected,
    open,
    onClose,
    history,
  } = props;

  const [answerScreenerSurveyMutation] = useMutation(ANSWER_SCREENER_SURVEY);
  const [answerConsentFormMutation] = useMutation(CONSENT_FORM_ANSWER_BY_SEGMENT_AND_PROJECT_ID);
  const [loading, setLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentQuestionAnswers, setCurrentQuestionAnswers] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [disqualified, setDisqualified] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(false);
  const [acceptsConsentForms, setAcceptsConsentForms] = useState();

  const { projectConsentForms = [] } = testerStudy;
  const consentForms = useMemo(() => _.map(projectConsentForms, ({ consentForm }) => consentForm), [projectConsentForms]);

  const [survey] = testerStudy.surveys;
  const { questions: screenerQuestions = [] } = survey || {};
  // Questions are being delivered out of order.
  const orderedQuestions = useMemo(() => _.orderBy(screenerQuestions, 'questionOrder'), [screenerQuestions]);
  const lastQuestion = isLastQuestion(currentQuestion, orderedQuestions);

  const disqualifyTester = (reason) => {
    onStudyRejected(testerStudy, reason);
    setDisqualified(true);
  };

  const redirectToQualifiedScreen = () => {
    history.push('/congratulations', {
      study: testerStudy,
    });
  };

  const saveScreenerSessionAnswers = async (updatedAnswers) => {
    try {
      setLoading(true);
      const {
        data: { answerScreenerSurvey: { tester } = {} } = {},
      } = await answerScreenerSurveyMutation({
        variables: {
          attributes: updatedAnswers,
          segmentId: testerStudy.id,
          projectId: testerStudy.projectId,
          surveyId: survey.id,
        },
      });

      if (tester.status === 'qualified') {
        redirectToQualifiedScreen();
      } else {
        disqualifyTester();
      }
    } catch (e) {
      captureException('ANSWER_SCREENER_SESSION_ERROR', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    const { id: questionId, questionOptions } = orderedQuestions[currentQuestion];

    // eslint-disable-next-line array-callback-return
    const newValues = currentQuestionAnswers.map((value) => ({
      questionId,
      questionOptionId: value,
    }));

    const updatedAnswers = [...answers, ...newValues];

    if (validateScreenerAnswers(currentQuestionAnswers, questionOptions) === screenerSessionResults.DISQUALIFIED) {
      saveScreenerSessionAnswers(updatedAnswers);
      return;
    }

    setAnswers(updatedAnswers);

    if (lastQuestion && updatedAnswers.length < screenerQuestions.length) {
      setSubmitError(true);
    }

    const questionHasAnswer = currentQuestionAnswers.length;

    if (lastQuestion && questionHasAnswer) {
      saveScreenerSessionAnswers(updatedAnswers);
    } else if (!lastQuestion && questionHasAnswer) {
      setCurrentQuestionAnswers([]);
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const isChecked = (id) => {
    if (currentQuestionAnswers.length > 0) {
      return currentQuestionAnswers.some((value) => value === id);
    }
    return false;
  };

  const hasScreener = !_.isEmpty(survey?.questions);
  const hasConsent = !_.isEmpty(projectConsentForms);

  if ((!hasScreener && !hasConsent)) {
    onClose();
    return false;
  }

  const { questionType, label, questionOptions = [] } = orderedQuestions[currentQuestion] || {};

  const handleChange = (value) => {
    if (currentQuestionAnswers.includes(value)) {
      const index = currentQuestionAnswers.indexOf(value);
      const newValue = [...currentQuestionAnswers];
      newValue.splice(index, 1);
      setCurrentQuestionAnswers(newValue);
    } else if (questionType === TYPE_QUESTIONS.MULTIPLE_SELECT) {
      const newValue = [...currentQuestionAnswers, value];
      setCurrentQuestionAnswers(newValue);
    } else {
      const newValue = [value];
      setCurrentQuestionAnswers(newValue);
    }
  };

  const handleAcceptConsentFormsResponse = async (acceptsConsentFormsResponse) => {
    setAcceptsConsentForms(acceptsConsentFormsResponse);
    try {
      setLoading(true);
      if (acceptsConsentFormsResponse) {
        // backend will check if the project have screeners, then we wont update the status to qualified yet.
        await answerConsentFormMutation({
          variables: {
            status: 'qualified',
            segmentId: testerStudy.id,
            projectId: testerStudy.projectId,
          },
        });
        if (!hasScreener) {
          redirectToQualifiedScreen();
        }
      } else if (!acceptsConsentFormsResponse) {
        disqualifyTester('consent_form');
      }
    } catch (e) {
      captureException('ANSWER_CONSENT_FORM_ERROR', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        size="small"
        open={open}
        closeOnEscape
        closeOnDimmerClick
        dimmer
        onClose={onClose}
      >
        <Container>
          <CloseIcon onClick={onClose} />
          {error && (
            <DisqualifiedContainer>
              {error}
            </DisqualifiedContainer>
          )}

          {disqualified ? (
            <DisqualifiedContainer>
              <Text>You did not qualify for this study.</Text>
            </DisqualifiedContainer>
          ) : (
            <>
              {!_.isEmpty(projectConsentForms) && !acceptsConsentForms && (
                <ConsentFormPrompt
                  consentForms={consentForms}
                  onSubmitAnswer={handleAcceptConsentFormsResponse}
                />
              )}

              {(!error && (!hasConsent || acceptsConsentForms)) && (
                loading ? (
                  <>
                    <Title>Redirecting</Title>
                    <CenteredLoader />
                  </>
                ) : (
                  // Screener Survey
                  <>
                    <Title>Screener Survey</Title>
                    <TextContainer>
                      <TextPrimaryBold>
                        {label}
                      </TextPrimaryBold>
                    </TextContainer>
                    <RadioGroup>
                      {questionOptions.map((questionOption) => (
                        <Options
                          key={questionOption.id}
                          label={questionOption.label}
                          onChange={() => handleChange(questionOption.id)}
                          checked={isChecked(questionOption.id)}
                          questionType={questionType}
                        />
                      ))}
                    </RadioGroup>

                    {submitError && (
                      <Error>Please select an answer</Error>
                    )}

                    <Footer>
                      <ButtonContainer>
                        <Button
                          disabled={currentQuestionAnswers.length <= 0 || loading}
                          onClick={handleClick}
                          loading={loading}
                        >
                          {lastQuestion ? 'Submit' : 'Next'}
                        </Button>
                      </ButtonContainer>
                      <QuestionCounter
                        current={currentQuestion + 1}
                        total={orderedQuestions.length}
                      />
                    </Footer>
                  </>
                )
              )}
            </>
          )}
        </Container>
      </Modal>
    </>
  );
};

ScreenerModal.propTypes = {
  testerStudy: PropTypes.shape({
    id: PropTypes.string.isRequired,
    projectId: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    device: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    surveys: PropTypes.arrayOf(PropTypes.shape({
      surveyType: PropTypes.string.isRequired,
      questions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        questionOptions: PropTypes.arrayOf(PropTypes.shape({
          disqualify: PropTypes.bool.isRequired,
          label: PropTypes.string.isRequired,
          optionType: PropTypes.string.isRequired,
          order: PropTypes.number.isRequired,
          qualify: PropTypes.bool.isRequired,
        })).isRequired,
      })),
    })),
    projectConsentForms: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      consentForm: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        filePath: PropTypes.string,
      })),
    })),
  }).isRequired,
  onStudyRejected: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

export default withRouter(ScreenerModal);
